import { Injectable } from '@angular/core';
import { IPDFDocumentDto, IPDFImagesDto } from 'core/dto';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { controllerEndpoints } from '../controller.endpoints';
import { crudEndpoints } from '../crud.endpoints';
import { customEndpoints } from '../custom.endpoints';
import joinUrls from '../utils.ts/join-urls';
import { ICrudAdvancedMethods } from './types/i-crud-advanced.methods';
import { ICrudBasicMethods } from './types/i-crud-basic.methods';
import { IGetByProcess } from './types/i-get-by-process';

@Injectable({
  providedIn: 'root'
})
export class PdfDocumentRemoteControllerService implements
  ICrudBasicMethods<IPDFDocumentDto>,
  IGetByProcess<IPDFDocumentDto>,
  Pick<
    ICrudAdvancedMethods<IPDFDocumentDto>,
    'reorder' | 'insertBatch'
  > {

  private readonly _endpoint = controllerEndpoints.PDF_DOCUMENT

  private readonly GET_BY_PROCESS_ID_WITH_METADATA = 'getDocumentsMetadataByProcessId'
  private readonly GET_BY_PROCESS_ID_IMAGES = 'getImagesByProcessId'
  private readonly DOWNLOAD_DOCUMENTS = 'getByDocumentIds'
  private readonly CAN_DOWNLOAD_BY_PROCESS_ID = 'canDownloadByProcessId'

  constructor(
    private readonly _apiService: ApiService
  ) { }

  getOne(id: string): Observable<IPDFDocumentDto> {
    const getOneEndpoint = joinUrls(this._endpoint, id)
    return this._apiService.get(getOneEndpoint)
  }
  update(id: string, updateDto: Partial<IPDFDocumentDto>): Observable<IPDFDocumentDto> {
    return this._apiService.post<IPDFDocumentDto>(this._endpoint, updateDto, { id });
  }

  create(createDto: Partial<IPDFDocumentDto>, ...params: unknown[]): Observable<string> {
    return this._apiService.post(this._endpoint, createDto);
  }
  remove(id: string): Observable<unknown> {
    const removeEndpoint = joinUrls(this._endpoint, id);
    return this._apiService.delete(removeEndpoint);
  }

  getByProcess(processId: string): Observable<IPDFDocumentDto[]> {
    const getByProcessEndpoint = joinUrls(this._endpoint, customEndpoints.GET_BY_PROCESS_ID)
    return this._apiService.get<IPDFDocumentDto[]>(getByProcessEndpoint, { processId });
  }

  reorder(reorderIds: string[], processId: string): Observable<unknown> {
    const reorderEndpoint = joinUrls(this._endpoint, crudEndpoints.REORDER, processId);
    return this._apiService.put<undefined>(reorderEndpoint, { ids: reorderIds });
  }

  insertBatch(insertDtoArray: Partial<IPDFDocumentDto>[]): Observable<IPDFDocumentDto[]> {
    const insertBatchEndpoint = joinUrls(this._endpoint);
    return this._apiService.post<IPDFDocumentDto[]>(insertBatchEndpoint, insertDtoArray);
  }

  getByProcessWithMetadata(processId: string): Observable<IPDFDocumentDto[]> {
    const getByProcessWithMetadataEndpoint = joinUrls(this._endpoint, this.GET_BY_PROCESS_ID_WITH_METADATA, processId);
    return this._apiService.get(getByProcessWithMetadataEndpoint)
  }

  getImagesByProcessId(processId: string, size: number, offset: number): Observable<IPDFImagesDto[]> {
    const getImagesByProcessIdEndpoint = joinUrls(this._endpoint, this.GET_BY_PROCESS_ID_IMAGES);
    return this._apiService.get(getImagesByProcessIdEndpoint, { size, offset, processId });
  }

  downloadDocuments(documentIds: string[]) {

    const downloadDocumentsEndpoint = joinUrls(this._endpoint, this.DOWNLOAD_DOCUMENTS);
    return this._apiService.get<IPDFDocumentDto[]>(downloadDocumentsEndpoint, { documentIds })
  }

  canDownloadByProcessId(processId: string) {
    const canDownloadByProcessIdEndpoint = joinUrls(this._endpoint, this.CAN_DOWNLOAD_BY_PROCESS_ID, processId);
    return this._apiService.get<{ canDownload: boolean }>(canDownloadByProcessIdEndpoint)
  }
}
